import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism'

import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Content, VectorType } from '@types'
import { ArtifactCard, InlineSource } from '@ui/chat'
import type { ReactPdfViewerParams } from '@ui/chat/modules/ReactPdfViewer'
import { SourceTooltipProvider } from '@ui/chat/SourceTooltipProvider'

import MermaidWrapper from './MermaidWrapper'
import Tooltip from './Tooltip'

export default function Markdown({
	children,
	contents,
	isWriting,
	onSourceSelected
}: {
	readonly children: string;
	readonly contents?: Content[] | undefined;
	readonly isWriting?: boolean | undefined;
	readonly onSourceSelected?: ((params: ReactPdfViewerParams) => void) | undefined;
}) {
	const cleanedContent = children.replace(
		/<table[^>]*>[\s\S]*?<\/table>/gi,
		tableMatch => tableMatch.split('\n')
			.map(line => line.trim())
			.filter(line => line.length)
			.join('\n')
			.replaceAll(/\*\*(.*?)\*\*/gm, '<b>$1</b>')
	)

	return (
		<SourceTooltipProvider>
			<ReactMarkdown
				components={ {
					code({ className, children, ...props }) {
						const match = /language-(\w+)/.exec(className ?? '')

						return match && match[1] === 'mermaid'
							? (
								<MermaidWrapper isWriting={ isWriting } mermaidCode={ String(children) } />
							)
							: match
								? (
									<SyntaxHighlighter
										useInlineStyles
										showLineNumbers={ false }
										style={ dracula }
										language={ match[1] }
										PreTag="div"
									>
										{ String(children).replace(/\n$/, '') }
									</SyntaxHighlighter>
								)
								: (
									<code { ...props } className="px-1 py-[1px] rounded-md bg-[#f0f0f0]">
										{ children }
									</code>
								)
					},
					source({ ...props }) {
						// Markdown makes all props to lowercase
						const { contentid, vectorid, page, excerpt, index, messageid, imageurl, type } = props as unknown as {
							excerpt: string;
							index: string;
							page: number;
							loading?: string | undefined;
							messageid?: string | undefined;
							contentid?: string | undefined;
							imageurl?: string | undefined;
							vectorid?: string | undefined;
							type?: VectorType | undefined;
						}

						const loading = 'loading' in props

						return (
							<InlineSource
								key={ `inline-source-${contentid}-${index}` }
								vectorId={ vectorid }
								page={ page }
								contentId={ contentid }
								messageId={ messageid }
								imageUrl={ imageurl }
								excerpt={ excerpt }
								index={ index }
								contents={ contents }
								isLoading={ Boolean(loading) }
								type={ type }
								onSourceSelected={ onSourceSelected }
							/>
						)
					},
					legend({ ...props }) {
						const { id, text } = props as unknown as {
							id: string;
							text: string;
						}

						const sentences = text.split('<br />')

						return (
							<>
								<FontAwesomeIcon
									data-tooltip={ id }
									icon="info-circle"
									className="text-gray-500 cursor-help" />

								<Tooltip
									anchor={ id }
									defaultPlace="right"
									offset={ 15 }
									customWidth="max-w-[700px]"
								>
									<div className="flex flex-col gap-2">
										{ sentences.map((sentence, index) => (
											<span key={ `legend-sentence-${index}` }>{ sentence }</span>
										)) }
									</div>
								</Tooltip>
							</>
						)
					},
					object({ ...props }) {
						const { id } = props as unknown as {id: string}

						return (
							<ArtifactCard id={ id } />
						)
					},
					a({ href, children, ...props }) {
						const isUrl = href?.startsWith('http://') || href?.startsWith('https://')

						if (isUrl) {
							return (
								// eslint-disable-next-line react/jsx-no-target-blank
								<a
									href={ href }
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 underline"
									{ ...props }
								>
									{ children }
								</a>
							)
						}

						// eslint-disable-next-line react/jsx-no-target-blank
						return <a href={ href } { ...props }>{ children }</a>
					}
				} }
				remarkPlugins={ [remarkGfm] }
				urlTransform={ (value: string) => value }
				rehypePlugins={ [rehypeRaw as any] }
			>
				{ cleanedContent }
			</ReactMarkdown>
		</SourceTooltipProvider>
	)
}
